/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import BreadCrumb from "components/Common/BreadCrumb/BreadCrumb";
import Select from "react-select"; // For blood group, allergies and habits
import CreatableSelect from "react-select/creatable";

/*import url and gateway methods */
import { putData, getData } from "utils/Gateway";
import * as url from "helper/UrlHelper";
import { getCustomStyles } from "Config/Config";

import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import {
  appUserHeaderLinks,
  appUserProfileLink,
} from "helper/AppUserHelper/AppUserHelper";
import { useTranslation } from "react-i18next";
import { getPublicIP } from "helper/AuthHelper/AuthHelper";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";
import bloodGroupOptions from "data/Prod/BloodGroups.json";

const AppUserMedicalBody = () => {
  const token = localStorage.getItem("token");
  const { t } = useTranslation(); //for translation
  const [name, setName] = useState("");
  const [surName, setSurName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // State to manage form inputs for medical extension
  const [medicalData, setMedicalData] = useState({
    insurance: false,
    insuranceNumber: "",
    insuranceCompany: "",
    insuranceExpirationDate: "", // Stored as YYYY-MM-DD string for input type="date"
    bloodgroup: null, // Changed to null for react-select
    allergies: [], // Array of { label: string, value: string } for CreatableSelect
    habits: [], // Array of { label: string, value: string } for CreatableSelect
    emergencycontact: "",
    emergencycontactname: "",
    emergencycontactrelation: "",
  });

  // Alert requirements
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // Hook for validation
  const [errors, setErrors] = useState({});
  const params = useParams();

  const commonHeaderObject = appUserHeaderLinks(params.id, t);

  /* ---- bread crumb text ---- */
  const breadcrumbText = [
    { title: t("App User"), link: appUserProfileLink },
    { title: t("Medical Information") },
  ];

  // Function to get user details and prefill medical data
  const getUserDetails = async () => {
    try {
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_USER_DETAILS + // Assuming this endpoint returns accountmedicalextension
        `/${params.id}` +
        ``;

      const response = await getData(requestUrl);

      console.log(response);

      if (response.status && response.data) {
        const data = response.data;

        setName(data.name || "");
        setSurName(data.surname || "");

        // Prefill medical data
        if (data.accountmedicalextension) {
          const medicalExt = data.accountmedicalextension;
          setMedicalData({
            insurance: medicalExt.insurance || false,
            insuranceNumber: medicalExt.insuranceNumber || "",
            insuranceCompany: medicalExt.insuranceCompany || "",
            insuranceExpirationDate: medicalExt.insuranceExpirationDate
              ? new Date(medicalExt.insuranceExpirationDate)
                  .toISOString()
                  .split("T")[0]
              : "",
            bloodgroup: medicalExt.bloodgroup
              ? { label: medicalExt.bloodgroup, value: medicalExt.bloodgroup }
              : null,
            allergies: medicalExt.allergies
              ? medicalExt.allergies.map((item) => ({
                  label: item,
                  value: item,
                }))
              : [],
            habits: medicalExt.habits
              ? medicalExt.habits.map((item) => ({
                  label: item,
                  value: item,
                }))
              : [],
            emergencycontact: medicalExt.emergencycontact || "",
            emergencycontactname: medicalExt.emergencycontactname || "",
            emergencycontactrelation: medicalExt.emergencycontactrelation || "",
          });
        }
      }
    } catch (error) {
      console.log(error.message);
      setMessageType("error");
      setAlertMessage(t("Failed to load user medical data."));
      setShowAlert(true);
    }
  };

  // Function to handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setMedicalData({
      ...medicalData,
      [name]: type === "checkbox" ? checked : value,
    });
    setErrors({}); // Clear errors on input change
  };

  // Function to handle Select and CreatableSelect changes
  const handleSelectChange = (selectedOptions, actionMeta) => {
    setMedicalData((prevMedicalData) => ({
      ...prevMedicalData,
      [actionMeta.name]: selectedOptions,
    }));
    setErrors({}); // Clear errors on input change
  };

  // Function to validate form inputs
  const validate = () => {
    let newErrors = {};
    let isValid = true;

    // Add validation rules here if needed
    // Example: if (!medicalData.insuranceNumber && medicalData.insurance) {
    //   newErrors.insuranceNumber = t("Insurance number is required.");
    //   isValid = false;
    // }

    setErrors(newErrors);
    return isValid;
  };

  // Function to handle form submission
  const saveMedicalDataHandler = async (e) => {
    e.preventDefault();
    if (validate()) {
      const userPublicIp = await getPublicIP();

      setIsLoading(true);
      try {
        // Prepare data for API call
        const dataToUpdate = {
          accountmedicalextension: {
            ...medicalData,
            bloodgroup: medicalData.bloodgroup
              ? medicalData.bloodgroup.value
              : "",
            allergies: medicalData.allergies.map((item) => item.value),
            habits: medicalData.habits.map((item) => item.value),
            // Ensure date is sent in a format the backend expects, e.g., ISO string
            insuranceExpirationDate: medicalData.insuranceExpirationDate
              ? new Date(medicalData.insuranceExpirationDate).toISOString()
              : "",
          },
        };

        let requestUrl =
          url.API_BASE_URL +
          url.API_UPDATE_APP_USER + // Assuming this is the endpoint to update user details
          `/${params.id}` +
          `?userip=${userPublicIp}`;

        const response = await putData(requestUrl, dataToUpdate);

        console.log(response);

        setIsLoading(false);
        if (response.status) {
          setMessageType("success");
          setAlertMessage(t("Medical information updated successfully."));
          setShowAlert(true);
        } else {
          setMessageType("error");
          setAlertMessage(t("Failed to update medical information."));
          setShowAlert(true);
        }
      } catch (error) {
        console.log(error.message);
        setMessageType("error");
        setAlertMessage(
          t("An error occurred while updating medical information.")
        );
        setShowAlert(true);
      }
    }
  };

  const onAlertClose = () => {
    setShowAlert(false);
    setAlertMessage("");
    setMessageType("");
  };

  useEffect(() => {
    if (params.id) {
      getUserDetails();
    }
  }, [params.id]);

  return (
    <div id="content_wrapper">
      <section className="event-details-wrapper bg-white pb-5">
        {/* ---- expert details common header ---- */}
        {params.id && (
          <TabsHeader
            commonHeaderObject={commonHeaderObject}
            activeOption={t("Medical")}
          />
        )}

        {/* ----- medical information form start -------- */}
        <div className="container-fluid px-3 px-lg-5 pt-4 pt-md-0">
          {/* ---- bread crumb ------- */}
          <BreadCrumb
            breadCrumbText={breadcrumbText}
            bottom={true}
            displayName={`${name} ${surName}`}
          />

          {/* ----- medical information form -------- */}
          <div className="p-3 p-lg-4 border border-gray-300 rounded-10 shadow-sm">
            <h3 className="mb-2">{t("Medical Information")}</h3>
            <p className="text-muted fs-sm mb-4">
              {t("Manage your medical details and emergency contacts.")}
            </p>

            <form onSubmit={saveMedicalDataHandler}>
              {/* Insurance Checkbox start */}
              <div className="row mb-4 align-items-center">
                <label
                  htmlFor="insurance"
                  className="col-md-3 col-form-label fw-semibold fs-sm"
                >
                  {t("Insurance")}
                </label>
                <div className="col-md-9">
                  <div className="form-check">
                    <input
                      className="form-check-input me-2"
                      type="checkbox"
                      id="insurance"
                      name="insurance"
                      checked={medicalData.insurance}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="insurance">
                      {t("Has Insurance")}
                    </label>
                  </div>
                </div>
              </div>
              {/* Insurance Checkbox end */}

              {/* Insurance Number Input start */}
              <div className="row mb-4">
                <label
                  htmlFor="insuranceNumber"
                  className="col-md-3 col-form-label fw-semibold fs-sm"
                >
                  {t("Insurance Number")}
                </label>
                <div className="col-md-9">
                  <input
                    type="text"
                    id="insuranceNumber"
                    name="insuranceNumber"
                    className={`form-control fs-sm shadow-none border border-gray-300 ${
                      errors.insuranceNumber ? "is-invalid" : ""
                    }`}
                    placeholder={t("Enter insurance number")}
                    value={medicalData.insuranceNumber}
                    onChange={handleInputChange}
                    disabled={!medicalData.insurance}
                  />
                  {errors.insuranceNumber && (
                    <div className="invalid-feedback">
                      {errors.insuranceNumber}
                    </div>
                  )}
                </div>
              </div>
              {/* Insurance Number Input end */}

              {/* Insurance Company Input start */}
              <div className="row mb-4">
                <label
                  htmlFor="insuranceCompany"
                  className="col-md-3 col-form-label fw-semibold fs-sm"
                >
                  {t("Insurance Company")}
                </label>
                <div className="col-md-9">
                  <input
                    type="text"
                    id="insuranceCompany"
                    name="insuranceCompany"
                    className={`form-control fs-sm shadow-none border border-gray-300 ${
                      errors.insuranceCompany ? "is-invalid" : ""
                    }`}
                    placeholder={t("Enter insurance company")}
                    value={medicalData.insuranceCompany}
                    onChange={handleInputChange}
                    disabled={!medicalData.insurance}
                  />
                  {errors.insuranceCompany && (
                    <div className="invalid-feedback">
                      {errors.insuranceCompany}
                    </div>
                  )}
                </div>
              </div>
              {/* Insurance Company Input end */}

              {/* Insurance Expiration Date Input start */}
              <div className="row mb-4">
                <label
                  htmlFor="insuranceExpirationDate"
                  className="col-md-3 col-form-label fw-semibold fs-sm"
                >
                  {t("Expiration Date")}
                </label>
                <div className="col-md-9">
                  <input
                    type="date"
                    id="insuranceExpirationDate"
                    name="insuranceExpirationDate"
                    className={`form-control fs-sm shadow-none border border-gray-300 ${
                      errors.insuranceExpirationDate ? "is-invalid" : ""
                    }`}
                    value={medicalData.insuranceExpirationDate}
                    onChange={handleInputChange}
                    disabled={!medicalData.insurance}
                  />
                  {errors.insuranceExpirationDate && (
                    <div className="invalid-feedback">
                      {errors.insuranceExpirationDate}
                    </div>
                  )}
                </div>
              </div>
              {/* Insurance Expiration Date Input end */}

              {/* Blood Group Dropdown start */}
              <div className="row mb-4">
                <label
                  htmlFor="bloodgroup"
                  className="col-md-3 col-form-label fw-semibold fs-sm"
                >
                  {t("Blood Group")}
                </label>
                <div className="col-md-9">
                  <Select
                    isClearable
                    id="bloodgroup"
                    name="bloodgroup"
                    options={bloodGroupOptions}
                    classNamePrefix="react-select"
                    value={medicalData.bloodgroup}
                    onChange={(selectedOption) =>
                      handleSelectChange(selectedOption, { name: "bloodgroup" })
                    }
                    placeholder={t("Select Blood Group")}
                    styles={getCustomStyles(errors.bloodgroup)}
                  />
                  {errors.bloodgroup && (
                    <div className="invalid-feedback d-block">
                      {errors.bloodgroup}
                    </div>
                  )}
                </div>
              </div>
              {/* Blood Group Dropdown end */}

              {/* Allergies Creatable Select start */}
              <div className="row mb-4">
                <label
                  htmlFor="allergies"
                  className="col-md-3 col-form-label fw-semibold fs-sm"
                >
                  {t("Allergies")}
                </label>
                <div className="col-md-9">
                  <CreatableSelect
                    isMulti
                    name="allergies"
                    options={[]} // Options can be dynamically loaded or left empty for creatable
                    classNamePrefix="react-select"
                    value={medicalData.allergies}
                    onChange={handleSelectChange}
                    placeholder={t("Type and press enter to add allergies")}
                    styles={getCustomStyles(errors.allergies)}
                  />
                  {errors.allergies && (
                    <div className="invalid-feedback d-block">
                      {errors.allergies}
                    </div>
                  )}
                </div>
              </div>
              {/* Allergies Creatable Select end */}

              {/* Habits Creatable Select start */}
              <div className="row mb-4">
                <label
                  htmlFor="habits"
                  className="col-md-3 col-form-label fw-semibold fs-sm"
                >
                  {t("Habits")}
                </label>
                <div className="col-md-9">
                  <CreatableSelect
                    isMulti
                    name="habits"
                    options={[]} // Options can be dynamically loaded or left empty for creatable
                    classNamePrefix="react-select"
                    value={medicalData.habits}
                    onChange={handleSelectChange}
                    placeholder={t("Type and press enter to add habits")}
                    styles={getCustomStyles(errors.habits)}
                  />
                  {errors.habits && (
                    <div className="invalid-feedback d-block">
                      {errors.habits}
                    </div>
                  )}
                </div>
              </div>
              {/* Habits Creatable Select end */}

              {/* Emergency Contact Input start */}
              <div className="row mb-4">
                <label
                  htmlFor="emergencycontact"
                  className="col-md-3 col-form-label fw-semibold fs-sm"
                >
                  {t("Emergency Contact")}
                </label>
                <div className="col-md-9">
                  <input
                    type="text"
                    id="emergencycontact"
                    name="emergencycontact"
                    className={`form-control fs-sm shadow-none border border-gray-300 ${
                      errors.emergencycontact ? "is-invalid" : ""
                    }`}
                    placeholder={t("Enter emergency contact number")}
                    value={medicalData.emergencycontact}
                    onChange={handleInputChange}
                  />
                  {errors.emergencycontact && (
                    <div className="invalid-feedback">
                      {errors.emergencycontact}
                    </div>
                  )}
                </div>
              </div>
              {/* Emergency Contact Input end */}

              {/* Emergency Contact Name Input start */}
              <div className="row mb-4">
                <label
                  htmlFor="emergencycontactname"
                  className="col-md-3 col-form-label fw-semibold fs-sm"
                >
                  {t("Emergency Contact Name")}
                </label>
                <div className="col-md-9">
                  <input
                    type="text"
                    id="emergencycontactname"
                    name="emergencycontactname"
                    className={`form-control fs-sm shadow-none border border-gray-300 ${
                      errors.emergencycontactname ? "is-invalid" : ""
                    }`}
                    placeholder={t("Enter emergency contact name")}
                    value={medicalData.emergencycontactname}
                    onChange={handleInputChange}
                  />
                  {errors.emergencycontactname && (
                    <div className="invalid-feedback">
                      {errors.emergencycontactname}
                    </div>
                  )}
                </div>
              </div>
              {/* Emergency Contact Name Input end */}

              {/* Emergency Contact Relation Input start */}
              <div className="row mb-4">
                <label
                  htmlFor="emergencycontactrelation"
                  className="col-md-3 col-form-label fw-semibold fs-sm"
                >
                  {t("Emergency Contact Relation")}
                </label>
                <div className="col-md-9">
                  <input
                    type="text"
                    id="emergencycontactrelation"
                    name="emergencycontactrelation"
                    className={`form-control fs-sm shadow-none border border-gray-300 ${
                      errors.emergencycontactrelation ? "is-invalid" : ""
                    }`}
                    placeholder={t("Enter relation to emergency contact")}
                    value={medicalData.emergencycontactrelation}
                    onChange={handleInputChange}
                  />
                  {errors.emergencycontactrelation && (
                    <div className="invalid-feedback">
                      {errors.emergencycontactrelation}
                    </div>
                  )}
                </div>
              </div>
              {/* Emergency Contact Relation Input end */}

              {/* Action Buttons start */}
              <div className="row">
                <div className="col-md-9 offset-md-3">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading}
                  >
                    {t("Save Medical Information")}
                    {isLoading && (
                      <div
                        className="ms-2 spinner-border spinner-border-sm"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    )}
                  </button>
                </div>
              </div>
              {/* Action Buttons end */}
            </form>
          </div>
        </div>
        {/* ----- medical information form end -------- */}
      </section>

      {showAlert && (
        <AlertNotification
          showAlert={showAlert}
          message={alertMessage}
          alertType={messageType}
          onClose={onAlertClose}
        />
      )}
    </div>
  );
};

export default AppUserMedicalBody;
